import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import BarChartIcon from '@mui/icons-material/BarChart';
import DonutSmallIcon from '@mui/icons-material/DonutSmall';
import ViewHeadlineIcon from '@mui/icons-material/ViewHeadline';
import AnalyticsFilters from './AnalyticsFilters';
import AnalyticsCard from './AnalyticsCard';
import NoResults from '../../../_helpers/reusablefunctions/NoResults';
import {
  createDefaultAnalyticsFilters,
  fetchInstagramAccountAnalytics,
  resolveAnalyticsPayload,
  resolveAnalyticsRows,
} from '../../../_services/profile/instagramAnalytics.service';
import './analytics.css';

const formatMetric = (value) => new Intl.NumberFormat('en-US').format(Number(value) || 0);

const formatShare = (value, total) =>
  `${total > 0 ? (((Number(value) || 0) / total) * 100).toFixed(1) : '0.0'}%`;

const regionNames = typeof Intl !== 'undefined' && Intl.DisplayNames
  ? new Intl.DisplayNames(['en'], { type: 'region' })
  : null;

const GENDER_LABELS = { F: 'Female', M: 'Male', U: 'Unknown' };

const BAR_COLOR = '#f0554f';
const DONUT_COLORS = ['#7b1113', '#a8161a', '#d12b2f', '#e8564f', '#f4938d'];
const OTHER_COLOR = '#9ca3af';

const BAR_LIMIT = 8;
const DONUT_LIMIT = 5;

const getLatestAnalytics = (rows) =>
  [...rows]
    .filter((row) => row)
    .sort((a, b) => new Date(b?.date || 0).getTime() - new Date(a?.date || 0).getTime())[0] || null;

const sumRows = (rows = []) => rows.reduce((sum, row) => sum + (Number(row?.value) || 0), 0);

const mapAudienceEntries = (source = {}) =>
  Object.entries(source || {})
    .map(([label, value]) => ({
      label,
      value: Number(value) || 0,
    }))
    .sort((first, second) => second.value - first.value);

const mapAudienceGenderAgeEntries = (source = {}) =>
  Object.entries(source || {})
    .map(([label, value]) => {
      const [ageRange = 'Unknown', gender = 'U'] = String(label || '').split(',');

      return {
        label,
        ageRange: ageRange || 'Unknown',
        gender: gender || 'U',
        genderLabel: GENDER_LABELS[gender] || 'Unknown',
        value: Number(value) || 0,
      };
    })
    .sort((first, second) => second.value - first.value);

const mapAudienceCountryEntries = (source = {}) =>
  Object.entries(source || {})
    .map(([countryCode, value]) => {
      const normalizedCode = String(countryCode || '').toUpperCase();
      const countryName = regionNames?.of(normalizedCode);

      return {
        label: countryName && countryName !== normalizedCode
          ? `${normalizedCode} - ${countryName}`
          : normalizedCode || 'Unknown',
        value: Number(value) || 0,
      };
    })
    .sort((first, second) => second.value - first.value);

const aggregateGenderRows = (rows = []) =>
  Object.values(GENDER_LABELS)
    .map((genderLabel) => ({
      label: genderLabel,
      value: rows
        .filter((row) => row?.genderLabel === genderLabel)
        .reduce((sum, row) => sum + (Number(row?.value) || 0), 0),
    }))
    .filter((row) => row.value > 0)
    .sort((first, second) => second.value - first.value);

const buildDonutSlices = (rows = []) => {
  const visible = rows.slice(0, DONUT_LIMIT).map((row, index) => ({
    ...row,
    color: DONUT_COLORS[index % DONUT_COLORS.length],
  }));

  const hiddenRows = rows.slice(DONUT_LIMIT);
  if (hiddenRows.length) {
    visible.push({
      label: `Other (${hiddenRows.length})`,
      value: sumRows(hiddenRows),
      color: OTHER_COLOR,
    });
  }

  return visible;
};

const buildConicGradient = (slices, total) => {
  if (!slices.length || total <= 0) return 'conic-gradient(#eceef2 0% 100%)';

  let offset = 0;
  const stops = slices.map((slice) => {
    const percentage = (slice.value / total) * 100;
    const stop = `${slice.color} ${offset}% ${offset + percentage}%`;
    offset += percentage;
    return stop;
  });

  return `conic-gradient(${stops.join(', ')})`;
};

const VIEW_OPTIONS = [
  { key: 'bar', label: 'Bar chart', Icon: BarChartIcon },
  { key: 'donut', label: 'Donut chart', Icon: DonutSmallIcon },
  { key: 'table', label: 'Table', Icon: ViewHeadlineIcon },
];

const AudiencePanel = ({ title, unit, rows = [], emptyText, tableColumns, tableRows }) => {
  const [view, setView] = useState('bar');

  const total = useMemo(() => sumRows(rows), [rows]);
  const maxValue = useMemo(
    () => rows.reduce((max, row) => Math.max(max, Number(row?.value) || 0), 0),
    [rows]
  );
  const donutSlices = useMemo(() => buildDonutSlices(rows), [rows]);

  const columns = tableColumns || [
    { key: 'label', label: title.replace(/^Audience\s+/i, '') },
    { key: 'value', label: 'Audience', align: 'right', format: formatMetric },
    { key: 'share', label: 'Share', align: 'right' },
  ];

  const bodyRows =
    tableRows || rows.map((row) => ({ ...row, share: formatShare(row.value, total) }));

  const hiddenBarCount = Math.max(rows.length - BAR_LIMIT, 0);

  return (
    <Box className="profile-audience-card">
      <Box className="profile-audience-head">
        <Box>
          <Typography className="profile-audience-title">{title}</Typography>
          <Typography className="profile-audience-subtitle">
            {formatMetric(total)} across {rows.length} {unit}
          </Typography>
        </Box>

        <Box className="profile-audience-views">
          {VIEW_OPTIONS.map((option) => (
            <button
              key={option.key}
              type="button"
              title={option.label}
              aria-label={option.label}
              aria-pressed={view === option.key}
              className={`profile-audience-view-btn${view === option.key ? ' active' : ''}`}
              onClick={() => setView(option.key)}
            >
              <option.Icon fontSize="inherit" />
            </button>
          ))}
        </Box>
      </Box>

      {!rows.length ? <Typography className="profile-audience-empty">{emptyText}</Typography> : null}

      {rows.length && view === 'bar' ? (
        <>
          <Box className="profile-audience-bars">
            {rows.slice(0, BAR_LIMIT).map((row) => (
              <Box key={`${title}-bar-${row.label}`} className="profile-audience-bar-row">
                <Typography className="profile-audience-bar-label" title={row.label}>
                  {row.label}
                </Typography>
                <Box className="profile-audience-bar-track">
                  <Box
                    className="profile-audience-bar-fill"
                    sx={{
                      width: maxValue > 0 ? `${(row.value / maxValue) * 100}%` : '0%',
                      backgroundColor: BAR_COLOR,
                    }}
                  />
                </Box>
                <Typography className="profile-audience-bar-value">{formatMetric(row.value)}</Typography>
                <Typography className="profile-audience-bar-share">
                  {formatShare(row.value, total)}
                </Typography>
              </Box>
            ))}
          </Box>

          {hiddenBarCount ? (
            <Typography className="profile-audience-more">
              +{hiddenBarCount} more — switch to Table for the full list.
            </Typography>
          ) : null}
        </>
      ) : null}

      {rows.length && view === 'donut' ? (
        <>
          <Box className="profile-audience-donut-wrap">
            <Box
              className="profile-audience-donut"
              sx={{ background: buildConicGradient(donutSlices, total) }}
            >
              <Box className="profile-audience-donut-center">
                <Typography className="profile-audience-donut-total">{formatMetric(total)}</Typography>
                <Typography className="profile-audience-donut-caption">audience</Typography>
              </Box>
            </Box>
          </Box>

          <Box className="profile-audience-legend">
            {donutSlices.map((slice) => (
              <Box key={`${title}-legend-${slice.label}`} className="profile-audience-legend-row">
                <Box className="profile-audience-legend-label">
                  <span
                    className="profile-audience-legend-dot"
                    style={{ backgroundColor: slice.color }}
                  />
                  <span className="profile-audience-legend-text" title={slice.label}>
                    {slice.label}
                  </span>
                </Box>
                <Typography className="profile-audience-bar-value">{formatMetric(slice.value)}</Typography>
                <Typography className="profile-audience-bar-share">
                  {formatShare(slice.value, total)}
                </Typography>
              </Box>
            ))}
          </Box>
        </>
      ) : null}

      {rows.length && view === 'table' ? (
        <Box className="profile-audience-table-wrap">
          <table className="profile-audience-table">
            <thead>
              <tr>
                {columns.map((column) => (
                  <th key={column.key} className={column.align === 'right' ? 'num' : ''}>
                    {column.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {bodyRows.map((row, index) => (
                <tr key={`${title}-row-${row.label || index}`}>
                  {columns.map((column) => (
                    <td key={column.key} className={column.align === 'right' ? 'num' : ''}>
                      {column.format ? column.format(row[column.key]) : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      ) : null}
    </Box>
  );
};

const AccountAnalytics = ({
  selectedHandleId = '',
  helperText,
  overrideAnalyticsData = null,
  hideFilters = false,
}) => {
  const [filters, setFilters] = useState(createDefaultAnalyticsFilters);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accountAnalytics, setAccountAnalytics] = useState(null);

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  useEffect(() => {
    setFilters((prev) => ({ ...prev, handlerId: String(selectedHandleId || '') }));
  }, [selectedHandleId]);

  useEffect(() => {
    if (overrideAnalyticsData) {
      setAccountAnalytics(overrideAnalyticsData);
      setError('');
      setLoading(false);
      return undefined;
    }

    let isMounted = true;

    const loadAnalytics = async () => {
      if (!selectedHandleId) {
        setAccountAnalytics(null);
        setError('');
        setLoading(false);
        return;
      }

      if (!filters.fromDate || !filters.toDate) {
        setError('Please select both dates.');
        setAccountAnalytics(null);
        setLoading(false);
        return;
      }

      if (filters.fromDate > filters.toDate) {
        setError('From date cannot be after to date.');
        setAccountAnalytics(null);
        setLoading(false);
        return;
      }

      setLoading(true);
      setError('');

      try {
        const response = await fetchInstagramAccountAnalytics({
          fromDate: filters.fromDate,
          toDate: filters.toDate,
          period: filters.period,
          handlerId: selectedHandleId,
        });

        if ((response?.status || 500) >= 400) {
          throw new Error('Unable to load account analytics.');
        }

        if (!isMounted) return;
        setAccountAnalytics(resolveAnalyticsPayload(response));
      } catch (fetchError) {
        if (!isMounted) return;
        setAccountAnalytics(null);
        setError('Failed to fetch account analytics. Please try again.');
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    loadAnalytics();

    return () => {
      isMounted = false;
    };
  }, [filters.fromDate, filters.toDate, filters.period, selectedHandleId, overrideAnalyticsData]);

  const latestAnalytics = useMemo(() => {
    const rows = resolveAnalyticsRows(accountAnalytics);
    return getLatestAnalytics(rows);
  }, [accountAnalytics]);

  const cards = useMemo(
    () => [
      {
        key: 'reach',
        title: 'Reach',
        value: formatMetric(latestAnalytics?.reach),
        icon: <VisibilityOutlinedIcon />,
      },
      {
        key: 'profile_views',
        title: 'Profile Views',
        value: formatMetric(latestAnalytics?.profile_views),
        icon: <PersonOutlineOutlinedIcon />,
      },
      {
        key: 'follower_count',
        title: 'Followers Count',
        value: formatMetric(latestAnalytics?.follower_count),
        icon: <GroupsOutlinedIcon />,
      },
    ],
    [latestAnalytics]
  );

  const audienceCityRows = useMemo(
    () => mapAudienceEntries(latestAnalytics?.audience_city),
    [latestAnalytics]
  );

  const audienceCountryRows = useMemo(
    () => mapAudienceCountryEntries(latestAnalytics?.audience_country),
    [latestAnalytics]
  );

  const audienceGenderAgeRows = useMemo(
    () => mapAudienceGenderAgeEntries(latestAnalytics?.audience_gender_age),
    [latestAnalytics]
  );

  const audienceGenderRows = useMemo(
    () => aggregateGenderRows(audienceGenderAgeRows),
    [audienceGenderAgeRows]
  );

  return (
    <Box className="profile-analytics-panel">
      {!hideFilters ? (
        <AnalyticsFilters
          filters={filters}
          onFilterChange={handleFilterChange}
          helperText={helperText || 'Account-level insights for the selected date range.'}
        />
      ) : helperText ? (
        <Typography variant="body2" className="profile-analytics-helper" sx={{ mb: 3 }}>
          {helperText}
        </Typography>
      ) : null}

      {error ? <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert> : null}

      <Box className="profile-analytics-card-grid">
        {(loading ? new Array(3).fill(null) : cards).map((card, index) => (
          <AnalyticsCard
            key={card?.key || index}
            icon={card?.icon}
            title={card?.title}
            value={card?.value}
            loading={loading}
          />
        ))}
      </Box>

      {!loading && !error && !selectedHandleId ? (
        <NoResults message="Select a connected account to view account analytics." />
      ) : null}

      {!loading && !error && selectedHandleId && !latestAnalytics ? (
        <NoResults message="No account analytics found for the selected date range." />
      ) : null}

      {!loading && latestAnalytics ? (
        <Box className="profile-audience-stack">
          <AudiencePanel
            title="Audience city"
            unit={audienceCityRows.length === 1 ? 'city' : 'cities'}
            rows={audienceCityRows}
            emptyText="No audience city data available."
          />

          <Box className="profile-audience-duo">
            <AudiencePanel
              title="Audience country"
              unit={audienceCountryRows.length === 1 ? 'country' : 'countries'}
              rows={audienceCountryRows}
              emptyText="No audience country data available."
            />
            <AudiencePanel
              title="Audience gender"
              unit={audienceGenderRows.length === 1 ? 'group' : 'groups'}
              rows={audienceGenderRows}
              emptyText="No audience gender data available."
              tableColumns={[
                { key: 'ageRange', label: 'Age' },
                { key: 'genderLabel', label: 'Gender' },
                { key: 'value', label: 'Audience', align: 'right', format: formatMetric },
              ]}
              tableRows={audienceGenderAgeRows}
            />
          </Box>
        </Box>
      ) : null}
    </Box>
  );
};

export default AccountAnalytics;
